import { useRef, type ChangeEvent, type DragEvent } from "react";
import { Button } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import { ACCEPTED_IMAGE_TYPES, MAX_IMAGE_MB } from "@admin/_helpers/productImageFile";
import type { PickedPromotionImage } from "../_helpers/usePromotionImage";

interface PromotionImageFieldProps {
    /** The image the promotion already has (edit), or null. */
    storedUrl: string | null;
    /** The file picked in this modal, previewed in place of the stored one. */
    picked: PickedPromotionImage | null;
    /** Why the last file offered was not taken, or null. */
    refusal: string | null;
    onPick: (files: File[]) => void;
    /** Drops the picked file: no image (create), the stored one (edit). */
    onClear: () => void;
    /** Locked while the save is in flight. */
    disabled: boolean;
}

// The promotion's one image: a drop zone that also opens the file picker,
// then a preview with Replace. Nothing uploads here; the save sends the file.
// The API can replace an image but never remove one, so an edit offers "Keep
// current image" (undo the pick), not "Remove".
export const PromotionImageField = ({
    storedUrl,
    picked,
    refusal,
    onPick,
    onClear,
    disabled,
}: PromotionImageFieldProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const previewUrl = picked?.previewUrl ?? storedUrl;
    const openPicker = () => fileInputRef.current?.click();

    const handlePick = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!disabled && files && files.length > 0) {
            onPick(Array.from(files));
        }
        // Allow picking the same file again after clearing it.
        event.target.value = "";
    };

    // Prevented on every drag, so a file dropped here is never opened by the
    // browser in place of the page.
    const handleDragOver = (event: DragEvent<HTMLDivElement>) =>
        event.preventDefault();

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (!disabled && event.dataTransfer.files.length > 0) {
            onPick(Array.from(event.dataTransfer.files));
        }
    };

    return (
        <div onDragOver={handleDragOver} onDrop={handleDrop}>
            <span className="font-semibold block mb-1">Image</span>
            {previewUrl ? (
                <>
                    <div className="aspect-[3/1] w-full rounded border border-base-300 overflow-hidden flex items-center justify-center">
                        <img
                            src={previewUrl}
                            alt="Promotion image preview"
                            className="h-full w-full object-contain"
                        />
                    </div>
                    <div className="mt-2 flex gap-2">
                        <Button
                            color={ComponentColor.Neutral}
                            size={ComponentSize.SM}
                            outline
                            disabled={disabled}
                            onClick={openPicker}
                        >
                            Replace image
                        </Button>
                        {picked && (
                            <Button
                                color={ComponentColor.Neutral}
                                size={ComponentSize.SM}
                                outline
                                disabled={disabled}
                                onClick={onClear}
                            >
                                {storedUrl ? "Keep current image" : "Remove image"}
                            </Button>
                        )}
                    </div>
                </>
            ) : (
                <button
                    type="button"
                    disabled={disabled}
                    className="aspect-[3/1] w-full rounded-lg border border-dashed border-base-300 bg-base-200 flex items-center justify-center text-sm text-gray-500"
                    onClick={openPicker}
                >
                    Drag an image here or click to upload
                </button>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_IMAGE_TYPES.join(",")}
                className="hidden"
                aria-label="Upload image"
                disabled={disabled}
                onChange={handlePick}
            />

            <p className="mt-1 text-xs text-gray-500">
                Optional. JPG, PNG, GIF, BMP or WebP, up to {MAX_IMAGE_MB} MB.
            </p>

            {/* A polite live region, rendered before any refusal so a screen
                reader announces the refusal put into it. */}
            <div role="status">
                {refusal && <p className="mt-1 text-sm text-error">{refusal}</p>}
            </div>
        </div>
    );
};
